import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { billingAPI, clientsAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  CurrencyDollar, Clock, CalendarBlank, Users, FileText, FunnelSimple, MagnifyingGlass
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const BillingPage = () => {
  const { user } = useAuth();
  const [billing, setBilling] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [filterClient, setFilterClient] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClients();
    loadBilling();
  }, []);

  const loadClients = async () => {
    try {
      const res = await clientsAPI.getAll();
      setClients(res.data);
    } catch (e) { /* silent */ }
  };

  const loadBilling = async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (filterClient !== 'all') params.client_id = filterClient;
      const res = await billingAPI.getAgencyBilling(params);
      setBilling(res.data);
    } catch (error) {
      console.error('Error loading billing:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => loadBilling();

  const filteredItems = billing?.line_items?.filter(item =>
    !searchTerm || item.client_name.toLowerCase().includes(searchTerm.toLowerCase()) || item.caregiver_name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (user?.role !== 'supervisor' && user?.role !== 'super_admin') {
    return <div className="flex flex-col items-center justify-center h-64"><CurrencyDollar weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" /><h3 className="font-heading text-lg">Access Restricted</h3></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="billing-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold">Billing</h1>
          <p className="text-muted-foreground mt-1">Track hours, rates, and generate billing reports</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border border-border" data-testid="billing-filters">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl" data-testid="billing-start-date" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl" data-testid="billing-end-date" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Client</Label>
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="rounded-xl" data-testid="billing-client-filter"><SelectValue placeholder="All Clients" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="rounded-xl gap-2" onClick={handleFilter} disabled={loading} data-testid="billing-apply-filter">
              <FunnelSimple weight="bold" className="w-4 h-4" />
              {loading ? 'Loading...' : 'Apply'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {billing && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="rounded-2xl border border-border" data-testid="billing-total-revenue">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Revenue</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">${billing.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <CurrencyDollar weight="duotone" className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border" data-testid="billing-total-hours">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Hours</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{billing.total_hours}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Clock weight="duotone" className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border border-border" data-testid="billing-total-visits">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Total Visits</p>
                  <h3 className="font-heading text-3xl font-semibold mt-2">{billing.total_visits}</h3>
                </div>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <CalendarBlank weight="duotone" className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Breakdown */}
      {billing?.client_breakdown?.length > 0 && (
        <Card className="rounded-2xl border border-border" data-testid="client-breakdown-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Client Breakdown</CardTitle>
            <CardDescription>Revenue by client for selected period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {billing.client_breakdown.map((cb, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-muted/30" data-testid={`client-breakdown-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Users weight="duotone" className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{cb.client_name}</p>
                      <p className="text-xs text-muted-foreground">{cb.visit_count} visits &middot; {cb.total_hours} hours</p>
                    </div>
                  </div>
                  <p className="font-heading font-semibold text-lg">${cb.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Line Items Table */}
      <Card className="rounded-2xl border border-border" data-testid="billing-line-items">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading text-lg">Billing Line Items</CardTitle>
              <CardDescription>{filteredItems.length} entries</CardDescription>
            </div>
            <div className="relative w-64">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 rounded-xl text-sm" data-testid="billing-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredItems.length === 0 ? (
            <div className="text-center py-12">
              <FileText weight="duotone" className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground text-sm">No billing entries found for this period</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Caregiver</TableHead>
                    <TableHead className="text-right">Hours</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item, i) => (
                    <TableRow key={item.visit_id} className={i % 2 === 0 ? '' : 'bg-muted/30'} data-testid={`billing-row-${item.visit_id}`}>
                      <TableCell className="text-sm">{item.date}</TableCell>
                      <TableCell className="text-sm font-medium">{item.client_name}</TableCell>
                      <TableCell className="text-sm">{item.caregiver_name}</TableCell>
                      <TableCell className="text-sm text-right">{item.hours}</TableCell>
                      <TableCell className="text-sm text-right">${item.rate.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-xs ${item.rate_type?.includes('overtime') ? 'bg-orange-100 text-orange-800' : item.rate_type === 'weekend' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>
                          {item.rate_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-right font-medium">${item.amount.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={`rounded-full text-xs ${item.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                          {item.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingPage;
